import { useEffect, useState } from "react";

import { useToast } from "@smart/bridge-intake-components-dom";
import { Modal, SegmentedControl } from "@smart/itops-sb-design-system-dom";
import { useAnalyticsEvent } from "@smart/manage-hooks-dom";

import {
  buildContactDetailsGroup,
  buildFieldsFromFieldSet,
  buildStandardNewFields,
  CustomisedContactDetailsField,
} from "./customise-helpers";
import {
  CustomiseContactDetailsModal,
  CustomisedContactDetailsInput,
} from "./customise-modal";
import { FieldSetRow } from "./field-set-row";
import { useContactRoleFields } from "./hooks";
import { RoleRow } from "./role-row";
import {
  GqlField,
  GqlGroup,
  GqlMatterLayout,
  GqlMatterType,
} from "../../../types";
import { fieldComponentIcons } from "../../constants";
import {
  CreateMappedFields,
  LoadMatterFields,
  MatterField,
  NewFieldOptions,
} from "../../types";
import { ContactType, getContactRoleLayouts } from "../../utils";

const contactTypeIndex: Record<ContactType, number> = {
  person: 0,
  company: 1,
};

const SectionHeading = ({ text }: { text: string }) => (
  <span className="px-[0.8rem] py-[0.8rem] text-caption font-semibold uppercase text-neutral-500 tracking-wide">
    {text}
  </span>
);
export type ContactDetailsEntryPoint =
  | "Contact Details Shortcut"
  | "Contact Details Component";

export type ContactDetailsOptions = {
  beforePosition?: string;
  afterPosition?: string;
  entryPoint: ContactDetailsEntryPoint;
};

const contactDetailsScreenComponent: Record<
  ContactDetailsEntryPoint,
  "Add Contact Details Shortcut" | "Add Contact Details Component"
> = {
  "Contact Details Shortcut": "Add Contact Details Shortcut",
  "Contact Details Component": "Add Contact Details Component",
};

const contactTypeLabel: Record<ContactType, "Person" | "Company"> = {
  person: "Person",
  company: "Company",
};

export type CustomisedContactDetailsItem = {
  uri: string;
  name: string;
  roleName: string;
  contactType: ContactType;
  fields: CustomisedContactDetailsField[];
};

export type ContactDetailsModalProps = {
  onClose: () => void;
  createFields: CreateMappedFields;
  fields: GqlField[];
  groups: GqlGroup[];
  matterLayouts?: GqlMatterLayout[];
  matterTypes?: GqlMatterType[];
  loadMatterFields: LoadMatterFields;
  contactDetailsOptions: ContactDetailsOptions | undefined;
  loading?: boolean;
  customisedContactDetails?: CustomisedContactDetailsItem[];
  onSaveCustomisedContactDetails?: (
    input: CustomisedContactDetailsInput,
  ) => Promise<CustomisedContactDetailsItem>;
  onDeleteCustomisedContactDetails?: (uri: string) => Promise<void>;
};

export const ContactDetailsModal = ({
  onClose,
  createFields,
  fields,
  groups,
  matterLayouts,
  matterTypes,
  loadMatterFields,
  contactDetailsOptions,
  loading,
  customisedContactDetails = [],
  onSaveCustomisedContactDetails,
  onDeleteCustomisedContactDetails,
}: ContactDetailsModalProps) => {
  const { publishEvent } = useAnalyticsEvent();
  const { setToasts } = useToast();
  const roleLayouts = contactDetailsOptions
    ? getContactRoleLayouts(matterLayouts || [])
    : [];
  const { matterFieldsByRole, isLoading } = useContactRoleFields({
    roleLayouts,
    matterTypes,
    loadMatterFields,
  });

  const [contactType, setContactType] = useState<ContactType>("person");
  const [isAdding, setIsAdding] = useState(false);
  const [isDeletingFieldSet, setIsDeletingFieldSet] = useState(false);
  const [isSavingFieldSet, setIsSavingFieldSet] = useState(false);
  const [saveFieldSetError, setSaveFieldSetError] = useState<string>();
  const [customising, setCustomising] = useState<{
    layout: GqlMatterLayout;
    matterFields: MatterField[];
    contactType: ContactType;
    initialFields?: NewFieldOptions[];
    editingFieldSet?: { uri: string; name: string };
  }>();

  const addFields = async (
    layout: GqlMatterLayout,
    newFields: NewFieldOptions[],
    fieldsContactType: ContactType,
    fieldsSource: "Default" | "Saved",
  ) => {
    if (!newFields.length) return;

    setIsAdding(true);
    try {
      await createFields({
        beforePosition: contactDetailsOptions?.beforePosition,
        afterPosition: contactDetailsOptions?.afterPosition,
        newFields,
        group: buildContactDetailsGroup({
          layout,
          contactType: fieldsContactType,
        }),
      });

      if (contactDetailsOptions) {
        publishEvent({
          category: "track",
          name: "IntakeQuickContactComponentAdded",
          screen: "FormBuilder",
          entryPoint: contactDetailsOptions.entryPoint,
          component:
            contactDetailsScreenComponent[contactDetailsOptions.entryPoint],
          jiraKey: "LINK-3759",
          interactionType: "Active",
          properties: {
            ContactType: contactTypeLabel[contactType],
            Fields: fieldsSource,
          },
        }).catch(console.error);
      }
      setCustomising(undefined);
      onClose();
    } catch (e) {
      console.error(e);
    } finally {
      setIsAdding(false);
    }
  };

  const onAdd = (layout: GqlMatterLayout) =>
    addFields(
      layout,
      buildStandardNewFields({
        layout,
        matterFields: matterFieldsByRole[layout.name] || [],
        contactType,
      }),
      contactType,
      "Default",
    );

  const onCustomiseBeforeAdding = (layout: GqlMatterLayout) =>
    setCustomising({
      layout,
      matterFields: matterFieldsByRole[layout.name] || [],
      contactType,
    });

  const onEditFieldSet = (entry: {
    item: CustomisedContactDetailsItem;
    roleLayout: GqlMatterLayout;
    matterFields: MatterField[];
    resolvedFields: NewFieldOptions[];
  }) =>
    setCustomising({
      layout: entry.roleLayout,
      matterFields: entry.matterFields,
      contactType: entry.item.contactType,
      initialFields: entry.resolvedFields,
      editingFieldSet: { uri: entry.item.uri, name: entry.item.name },
    });

  const resolveFieldSet = (item: CustomisedContactDetailsItem) => {
    const roleLayout = roleLayouts.find(
      (layout) => layout.name === item.roleName,
    );
    if (!roleLayout) return undefined;

    const matterFields = matterFieldsByRole[item.roleName] || [];
    const resolvedFields = buildFieldsFromFieldSet({
      layout: roleLayout,
      matterFields,
      fields: item.fields,
    });
    if (!resolvedFields.length) return undefined;

    return { roleLayout, matterFields, resolvedFields };
  };

  const visibleFieldSets = customisedContactDetails
    .filter(
      (item) =>
        item.contactType === contactType &&
        roleLayouts.some((layout) => layout.name === item.roleName),
    )
    .map((item) => {
      const resolved = resolveFieldSet(item);
      if (!resolved) return undefined;
      return {
        item,
        ...resolved,
        customisedFields: resolved.resolvedFields.map((field) => ({
          fieldName: field.field?.name || "",
          label: field.label || "",
        })),
      };
    })
    .filter(
      (
        entry,
      ): entry is {
        item: CustomisedContactDetailsItem;
        roleLayout: GqlMatterLayout;
        matterFields: MatterField[];
        resolvedFields: NewFieldOptions[];
        customisedFields: CustomisedContactDetailsField[];
      } => !!entry,
    );

  const onSaveFieldSet = onSaveCustomisedContactDetails
    ? async (input: CustomisedContactDetailsInput) => {
        setIsSavingFieldSet(true);
        setSaveFieldSetError(undefined);
        try {
          await onSaveCustomisedContactDetails(input);
          if (!input.uri) {
            publishEvent({
              category: "track",
              name: "IntakeSaveReusableContact",
              screen: "FormBuilder",
              entryPoint: "SaveCustomisedContactDetails",
              component: "ContactDetailsComponent",
              jiraKey: "LINK-3761",
              interactionType: "Active",
              properties: {
                ContactType: contactTypeLabel[input.contactType],
                RoleName: input.roleName,
                NumberOfFields: input.fields.length,
              },
            }).catch(console.error);
          } else {
            setToasts([{ text: "Field set updated", palette: "success" }]);
          }
          setCustomising(undefined);
        } catch (e) {
          console.error(e);
          setSaveFieldSetError(
            e instanceof Error ? e.message : "Failed to save field set.",
          );
          throw e;
        } finally {
          setIsSavingFieldSet(false);
        }
      }
    : undefined;

  const onDeleteFieldSet = onDeleteCustomisedContactDetails
    ? async (uri: string) => {
        setIsDeletingFieldSet(true);
        try {
          await onDeleteCustomisedContactDetails(uri);
          setToasts([
            {
              text: "Field set deleted",
              palette: "success",
            },
          ]);
        } catch (e) {
          console.error(e);
          setToasts([
            {
              text:
                e instanceof Error ? e.message : "Failed to delete field set.",
              palette: "destructive",
            },
          ]);
        } finally {
          setIsDeletingFieldSet(false);
        }
      }
    : undefined;

  useEffect(() => {
    setContactType("person");
    setIsAdding(false);
    setIsDeletingFieldSet(false);
    setIsSavingFieldSet(false);
    setSaveFieldSetError(undefined);
    setCustomising(undefined);
  }, [contactDetailsOptions]);

  const hasCustomisedFieldSets = visibleFieldSets.length > 0;
  const fieldSetsDisabled = isAdding || isDeletingFieldSet;

  return (
    <>
      {!!contactDetailsOptions && !customising && (
        <Modal
          className="[&_.modal-container]:min-h-[min(90rem,95vh)]"
          open
          onClose={onClose}
          loading={loading || isAdding || isDeletingFieldSet}
          header={{
            icon: fieldComponentIcons.contactDetails.name,
            iconBgColor: fieldComponentIcons.contactDetails.bgColor,
            text: "Add contact details",
          }}
        >
          <div className="flex items-center gap-medium">
            <span className="font-semibold text-ui text-neutral-850">
              Contact is a
            </span>
            <SegmentedControl
              index={contactTypeIndex[contactType]}
              items={[
                {
                  label: "Person",
                  icon: "regularCircleUser",
                  onClick: () => setContactType("person"),
                },
                {
                  label: "Company",
                  icon: "regularBuildingColumns",
                  onClick: () => setContactType("company"),
                },
              ]}
            />
          </div>
          <div className="flex flex-col">
            {hasCustomisedFieldSets && (
              <div className="flex flex-col">
                <SectionHeading text="Your field sets" />
                {visibleFieldSets.map(
                  ({
                    item,
                    roleLayout,
                    matterFields,
                    resolvedFields,
                    customisedFields,
                  }) => (
                    <FieldSetRow
                      key={item.uri}
                      name={item.name}
                      layoutId={roleLayout.id}
                      roleDisplayName={
                        roleLayout.displayName || roleLayout.name
                      }
                      contactType={item.contactType}
                      fields={customisedFields}
                      disabled={fieldSetsDisabled}
                      formFields={fields}
                      formGroups={groups}
                      onAdd={() =>
                        addFields(
                          roleLayout,
                          resolvedFields,
                          item.contactType,
                          "Saved",
                        )
                      }
                      onEdit={() =>
                        onEditFieldSet({
                          item,
                          roleLayout,
                          matterFields,
                          resolvedFields,
                        })
                      }
                      onDelete={() => onDeleteFieldSet?.(item.uri)}
                    />
                  ),
                )}
              </div>
            )}
            {hasCustomisedFieldSets && roleLayouts.length > 0 && (
              <>
                <div className="border-t border-solid border-neutral-100 mt-[0.4rem] mb-[0.8rem]" />
                <SectionHeading text="Contact types" />
              </>
            )}
            {roleLayouts.map((layout) => (
              <RoleRow
                key={layout.name}
                layout={layout}
                matterFields={matterFieldsByRole[layout.name] || []}
                contactType={contactType}
                isLoading={isLoading}
                disabled={fieldSetsDisabled}
                formFields={fields}
                formGroups={groups}
                onAdd={onAdd}
                onCustomiseBeforeAdding={onCustomiseBeforeAdding}
              />
            ))}
          </div>
        </Modal>
      )}
      {!!contactDetailsOptions && !!customising && (
        <CustomiseContactDetailsModal
          layout={customising.layout}
          matterFields={customising.matterFields}
          contactType={customising.contactType}
          initialFields={customising.initialFields}
          isSaving={isAdding}
          isSavingFieldSet={isSavingFieldSet}
          saveFieldSetError={saveFieldSetError}
          editingFieldSet={customising.editingFieldSet}
          savedFieldSets={customisedContactDetails}
          onCancel={() => {
            setSaveFieldSetError(undefined);
            setCustomising(undefined);
          }}
          onConfirm={(layout, newFields) =>
            addFields(layout, newFields, customising.contactType, "Saved")
          }
          onSaveFieldSet={onSaveFieldSet}
        />
      )}
    </>
  );
};
